import React from 'react';
import {
  formatString,
  SCHRODINGER_DURATION_MAX_DEFAULT,
  SCHRODINGER_DURATION_MIN_DEFAULT,
  schrodingerObserveExplosionPercent,
  type SchrodingerRoleChoice,
} from '@deadpan/shared';
import { useSocket } from '../../contexts/SocketContext';
import { useTranslations } from '../../hooks/useTranslations';

/** Two players sit at the ends of a horizontal line. Three or more sit on a regular polygon, first seat at the top, then clockwise. */
function circleLayout(count: number): { width: number; height: number; points: { x: number; y: number }[] } {
  const radius = 40;
  const labelPad = count <= 3 ? 16 : count <= 5 ? 13 : 11;
  const topPad = 10;
  const bottomPad = 26;
  const raw = Array.from({ length: count }, (_, index) => {
    const start = count === 2 ? Math.PI : -Math.PI / 2;
    const angle = start + (2 * Math.PI * index) / count;
    return { x: radius * Math.cos(angle), y: radius * Math.sin(angle) };
  });
  const minX = Math.min(...raw.map((point) => point.x));
  const maxX = Math.max(...raw.map((point) => point.x));
  const minY = Math.min(...raw.map((point) => point.y));
  const maxY = Math.max(...raw.map((point) => point.y));
  return {
    width: maxX - minX + labelPad * 2,
    height: maxY - minY + topPad + bottomPad,
    points: raw.map((point) => ({
      x: point.x - minX + labelPad,
      y: point.y - minY + topPad,
    })),
  };
}

export const SchrodingerCrocGame: React.FC = () => {
  const {
    socket,
    currentRoom,
    gameState,
    mySchrodingerObservation,
    myPlayerId,
  } = useSocket();
  const t = useTranslations();
  const sc = t.games.schrodingerCroc;

  const stage = gameState?.schrodingerStage;
  const round = currentRoom?.currentRound ?? 1;
  const minSeconds = gameState?.schrodingerMinSeconds ?? currentRoom?.nashConfig?.schrodingerMinSeconds ?? SCHRODINGER_DURATION_MIN_DEFAULT;
  const maxSeconds = gameState?.schrodingerMaxSeconds ?? currentRoom?.nashConfig?.schrodingerMaxSeconds ?? SCHRODINGER_DURATION_MAX_DEFAULT;
  const [armDraft, setArmDraft] = React.useState('');

  React.useEffect(() => {
    if (stage === 'arm') {
      setArmDraft(String(minSeconds));
    }
  }, [round, stage, minSeconds]);

  if (!gameState || !currentRoom) {
    return null;
  }

  const playersById = Object.fromEntries(currentRoom.players.map((player) => [player.id, player]));
  const order = gameState.schrodingerOrder ?? [];
  const crocodileId = gameState.schrodingerCrocodileId;
  const holderId = gameState.schrodingerHolderId;
  const winnerId = stage === 'result' ? gameState.schrodingerWinnerId : undefined;
  const loserId = stage === 'result' ? gameState.schrodingerLoserId : undefined;
  const askerId = stage === 'result' ? gameState.schrodingerAskerId : undefined;
  const answererId = stage === 'result' ? gameState.schrodingerAnswererId : undefined;
  const explodedId = stage === 'result' ? gameState.schrodingerExplodedPlayerId : undefined;
  const nameOf = (id?: string) => (id ? playersById[id]?.name ?? '—' : '—');
  const isHost = currentRoom.hostId === myPlayerId;
  const isSpectator = Boolean(currentRoom.players.find((player) => player.id === myPlayerId)?.isSpectator);
  const isCrocodile = Boolean(myPlayerId && myPlayerId === crocodileId);
  const isHolder = Boolean(myPlayerId && myPlayerId === holderId);
  const isWinner = Boolean(myPlayerId && myPlayerId === winnerId);
  const rolesChosen = Boolean(askerId && answererId);
  const passCount = gameState.schrodingerPassCount ?? 0;
  const holdId = gameState.schrodingerHoldId ?? 0;
  const alreadyObserved = Boolean(gameState.schrodingerHolderObserved);
  const explosionPercent = schrodingerObserveExplosionPercent(passCount);
  const reading = stage === 'live'
    && isHolder
    && mySchrodingerObservation
    && mySchrodingerObservation.holdId === holdId
    ? mySchrodingerObservation.remainingSeconds
    : null;
  const parsedArmSeconds = Number(armDraft);
  const canArm = armDraft !== ''
    && Number.isInteger(parsedArmSeconds)
    && parsedArmSeconds >= minSeconds
    && parsedArmSeconds <= maxSeconds;

  const handleArm = () => {
    if (!socket || stage !== 'arm' || !isCrocodile || isSpectator || !canArm) return;
    socket.emit('client:schrodinger-arm', parsedArmSeconds);
  };

  const handlePass = () => {
    if (!socket || stage !== 'live' || !isHolder || isSpectator) return;
    socket.emit('client:schrodinger-pass');
  };

  const handleObserve = () => {
    if (!socket || stage !== 'live' || !isHolder || isSpectator || alreadyObserved) return;
    socket.emit('client:schrodinger-observe');
  };

  const handleChooseRole = (choice: SchrodingerRoleChoice) => {
    if (!socket || !isWinner || rolesChosen) return;
    socket.emit('client:schrodinger-choose-role', choice);
  };

  const handleNextRound = () => {
    if (!socket || !isHost) return;
    socket.emit('client:schrodinger-next-round');
  };

  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 w-[90%] max-w-[600px] border border-white/20 flex flex-col items-center">
      <h2 className="text-[clamp(1.5rem,8vw,3rem)] font-bold leading-tight bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 bg-clip-text text-transparent text-center mb-1">
        {sc.title}
      </h2>
      <p className="text-xs sm:text-sm text-gray-500 mb-3">
        {formatString(sc.roundLabel, { round: String(round) })}
      </p>

      <div className="w-full mb-4">
        <h3 className="text-sm font-semibold text-gray-700 text-center">{sc.circleTitle}</h3>
        <p className="text-xs text-gray-500 text-center mb-2">{sc.circleHint}</p>
        <PlayerCircle
          order={order}
          crocodileId={crocodileId}
          holderId={stage === 'arm' ? undefined : holderId}
          youId={myPlayerId ?? undefined}
          nameOf={nameOf}
          youBadge={sc.youBadge}
          crocodileBadge={sc.crocodileBadge}
          bombBadge={sc.bombBadge}
        />
      </div>

      {stage === 'arm' && (
        <div className="w-full flex flex-col items-center gap-3">
          {isCrocodile && !isSpectator ? (
            <>
              <h3 className="text-base sm:text-lg font-bold text-gray-900 text-center">{sc.armTitle}</h3>
              <p className="text-sm text-gray-600 text-center">
                {formatString(sc.armHint, { min: String(minSeconds), max: String(maxSeconds) })}
              </p>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                enterKeyHint="done"
                aria-label={sc.armTitle}
                placeholder={`${minSeconds}–${maxSeconds}`}
                value={armDraft}
                onChange={(event) => setArmDraft(event.target.value.replace(/\D/g, ''))}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') handleArm();
                }}
                className="w-full rounded-xl border-2 border-emerald-300 bg-white px-4 py-3 text-center text-[clamp(1.5rem,8vw,3rem)] font-bold text-emerald-700 outline-none focus:border-emerald-500"
              />
              <button
                type="button"
                onClick={handleArm}
                disabled={!canArm}
                className="w-full px-4 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 disabled:cursor-not-allowed disabled:from-gray-400 disabled:to-gray-400"
              >
                {sc.confirmArm}
              </button>
            </>
          ) : (
            <p className="text-sm text-gray-600 text-center">
              {isSpectator
                ? sc.spectatorWatching
                : formatString(sc.armWaiting, { name: nameOf(crocodileId) })}
            </p>
          )}
        </div>
      )}

      {stage === 'live' && (
        <div className="w-full flex flex-col items-center gap-3">
          <p className="text-sm font-semibold text-emerald-800 text-center">
            {formatString(sc.passLabel, {
              pass: String(passCount + 1),
              percent: String(explosionPercent),
            })}
          </p>
          <p className="text-base sm:text-lg font-bold text-gray-900 text-center">
            {isHolder ? sc.youHold : formatString(sc.holding, { name: nameOf(holderId) })}
          </p>
          {reading != null && (
            <div className="text-center">
              <p className="text-[clamp(1.5rem,8vw,3rem)] font-bold text-emerald-700 whitespace-nowrap">
                {reading.toFixed(1)}s
              </p>
              <p className="text-xs text-gray-500">{sc.readingFrozen}</p>
            </div>
          )}
          {isHolder && !isSpectator ? (
            <div className="w-full flex flex-col gap-2">
              <button
                type="button"
                onClick={handlePass}
                className="w-full px-4 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
              >
                {sc.pass}
              </button>
              <button
                type="button"
                onClick={handleObserve}
                disabled={alreadyObserved}
                className="w-full px-4 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed"
              >
                {alreadyObserved ? sc.observed : sc.observe}
              </button>
            </div>
          ) : isSpectator ? (
            <p className="text-sm text-gray-600 text-center">{sc.spectatorWatching}</p>
          ) : null}
        </div>
      )}

      {stage === 'result' && (
        <div className="w-full mt-1 space-y-4">
          <h3 className="text-xl sm:text-2xl font-bold text-center text-gray-900">{sc.resultTitle}</h3>
          <div className="rounded-xl border-2 border-rose-300 bg-rose-50 p-3 text-center">
            <p className="text-lg font-bold text-rose-900">
              {formatString(sc.explodedTitle, { name: nameOf(explodedId) })}
            </p>
            <p className="text-sm text-rose-800 mt-1">
              {gameState.schrodingerExplosionCause === 'observe' ? sc.causeObserve : sc.causeTimer}
            </p>
          </div>
          {rolesChosen ? (
            <>
              <p className="text-center text-base sm:text-lg font-semibold text-gray-800">
                {formatString(sc.askPrompt, { asker: nameOf(askerId), answerer: nameOf(answererId) })}
              </p>
              {myPlayerId === askerId && (
                <p className="text-center text-sm font-semibold text-indigo-700">{sc.youAreAsker}</p>
              )}
              {myPlayerId === answererId && (
                <p className="text-center text-sm font-semibold text-pink-700">{sc.youAreAnswerer}</p>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-xl border-2 border-red-200 bg-red-50 p-3 text-center">
                  <p className="text-xs font-semibold text-red-700">{sc.askerLabel}</p>
                  <p className="text-lg font-bold text-red-900 break-words">{nameOf(askerId)}</p>
                </div>
                <div className="rounded-xl border-2 border-amber-200 bg-amber-50 p-3 text-center">
                  <p className="text-xs font-semibold text-amber-700">{sc.answererLabel}</p>
                  <p className="text-lg font-bold text-amber-900 break-words">{nameOf(answererId)}</p>
                </div>
              </div>
            </>
          ) : (
            <>
              <p className="text-center text-base sm:text-lg font-semibold text-gray-800">
                {isWinner
                  ? sc.chooserChooseHint
                  : formatString(sc.waitingForChooser, { chooser: nameOf(winnerId) })}
              </p>
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-xl border-2 border-pink-200 bg-pink-50 p-3 min-h-[3.5rem] flex items-center justify-center text-center">
                  <p className="text-lg font-bold text-pink-900 break-words">{nameOf(winnerId)}</p>
                </div>
                <div className="rounded-xl border-2 border-gray-200 bg-gray-50 p-3 min-h-[3.5rem] flex items-center justify-center text-center">
                  <p className="text-lg font-bold text-gray-900 break-words">{nameOf(loserId)}</p>
                </div>
              </div>
              {isWinner && (
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={() => handleChooseRole('ask')}
                    className="w-full px-4 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700"
                  >
                    {sc.chooseAsk}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleChooseRole('answer')}
                    className="w-full px-4 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700"
                  >
                    {sc.chooseAnswer}
                  </button>
                </div>
              )}
            </>
          )}
          {rolesChosen && isHost ? (
            <button
              type="button"
              onClick={handleNextRound}
              className="w-full px-4 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
            >
              {sc.nextRound}
            </button>
          ) : rolesChosen ? (
            <p className="text-sm text-gray-600 text-center">{sc.waitingForHostNextRound}</p>
          ) : null}
        </div>
      )}
    </div>
  );
};

function SeatFace({ crocodile }: { crocodile: boolean }) {
  return (
    <div className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-[#7dcec6] bg-[#f7f3ea]">
      {crocodile ? (
        <span className="text-[1.35rem] leading-none" aria-hidden="true">🐊</span>
      ) : (
        <svg viewBox="0 0 48 48" className="h-7 w-7" aria-hidden="true">
          <circle cx="24" cy="16" r="7" fill="#8b93a1" />
          <path fill="#8b93a1" d="M8 42c1.2-9 7.2-14 16-14s14.8 5 16 14H8z" />
        </svg>
      )}
    </div>
  );
}

function PlayerCircle({
  order,
  crocodileId,
  holderId,
  youId,
  nameOf,
  youBadge,
  crocodileBadge,
  bombBadge,
}: {
  order: string[];
  crocodileId?: string;
  holderId?: string;
  youId?: string;
  nameOf: (id?: string) => string;
  youBadge: string;
  crocodileBadge: string;
  bombBadge: string;
}) {
  const count = order.length;
  if (count === 0) return null;

  const layout = circleLayout(count);
  const labelWidth = count <= 3 ? 'w-20' : count <= 5 ? 'w-16' : 'w-14';

  return (
    <div className="mx-auto w-full max-w-[420px] [container-type:inline-size]">
      <ol
        className="relative m-0 w-full list-none p-0"
        style={{ height: `${(layout.height / layout.width) * 100}cqw` }}
      >
        {order.map((playerId, index) => {
          const point = layout.points[index];
          const isCrocodile = playerId === crocodileId;
          const isHolder = playerId === holderId;
          const isYou = playerId === youId;
          const label = [
            nameOf(playerId),
            isYou ? youBadge : '',
            isCrocodile ? crocodileBadge : '',
            isHolder ? bombBadge : '',
          ].filter(Boolean).join(' ');
          return (
            <li
              key={playerId}
              aria-label={label}
              className="absolute h-11 w-11 -translate-x-1/2 -translate-y-1/2"
              style={{ left: `${(point.x / layout.width) * 100}cqw`, top: `${(point.y / layout.width) * 100}cqw` }}
            >
              <SeatFace crocodile={isCrocodile} />
              {isHolder && (
                <span className="pointer-events-none absolute -right-5 top-1/2 -translate-y-1/2 text-base leading-none" aria-hidden="true">
                  💣
                </span>
              )}
              <div className={`absolute left-1/2 top-full mt-0.5 -translate-x-1/2 text-center ${labelWidth}`}>
                <p className="truncate text-[11px] font-semibold leading-tight text-gray-900">
                  {nameOf(playerId)}
                </p>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
